import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, isRTL } from './i18n';

const STORAGE_KEY = 'flashusdt:language';
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface GeoDetectResponse {
    success: boolean;
    language: string;
    country: string;
    countryCode: string;
}

/**
 * Hook to detect and manage user language based on IP geolocation
 */
export function useGeoLanguage() {
    const { i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [detectedCountry, setDetectedCountry] = useState<string | null>(null);

    useEffect(() => {
        const detectLanguage = async () => {
            // Check if user has a stored preference
            const storedLang = localStorage.getItem(STORAGE_KEY);
            if (storedLang && SUPPORTED_LANGUAGES.some(l => l.code === storedLang)) {
                await i18n.changeLanguage(storedLang);
                applyDirection(storedLang);
                setIsLoading(false);
                return;
            }

            // Try to detect from API
            try {
                const response = await fetch(`${API_BASE_URL}/api/geo/detect`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });

                if (response.ok) {
                    const data: GeoDetectResponse = await response.json();
                    if (data.success && data.language) {
                        await i18n.changeLanguage(data.language);
                        localStorage.setItem(STORAGE_KEY, data.language);
                        setDetectedCountry(data.country);
                        applyDirection(data.language);
                    }
                }
            } catch (error) {
                console.warn('Geo language detection failed, using default:', error);
            }

            setIsLoading(false);
        };

        detectLanguage();
    }, [i18n]);

    /**
     * Apply text direction based on language
     */
    const applyDirection = (langCode: string) => {
        const dir = isRTL(langCode) ? 'rtl' : 'ltr';
        document.documentElement.setAttribute('dir', dir);
        document.documentElement.setAttribute('lang', langCode);
    };

    /**
     * Change language manually
     */
    const changeLanguage = async (langCode: string) => {
        if (SUPPORTED_LANGUAGES.some(l => l.code === langCode)) {
            await i18n.changeLanguage(langCode);
            localStorage.setItem(STORAGE_KEY, langCode);
            applyDirection(langCode);
        }
    };

    return {
        currentLanguage: i18n.language,
        isLoading,
        detectedCountry,
        changeLanguage,
        supportedLanguages: SUPPORTED_LANGUAGES,
    };
}
